import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { jwtDecode, JwtPayload as JwtPayloadBase } from "jwt-decode";
import {
  auth,
  AuthForm,
  enrollmentOtp as enrollmentOtpApi,
  EnrollmentOtpForm,
  refreshToken,
  register as registerApi,
  RegisterForm,
} from "./api";
import { FetchClientError } from "@request/client";

type Role =
  | "administrator"
  | "customer_support"
  | "merchant"
  | "super_user"
  | "trader"
  | "trader_teamlead"
  | "merchant_agent"
  | "not_verified"
  | "need_verify_otp";

export interface JwtPayload extends JwtPayloadBase {
  email_verified: boolean;
  roles?: Role[];
  status?: string;
  user_id: string;
  commission_withdrawal?: number;
  email: string;
  sign_in_provider: "password";
  sign_in_second_factor?: "totp";
  allow_farm_creation?: boolean;
  allow_requisites_without_device?: boolean;
}

export type AuthState = {
  roles?: Role[] | null;
  status?: string | null;
  accessToken?: string | null;
  refreshToken?: string | null;
  qrCodeUrl?: string | null;
  sessionInfo?: string | null;
  userId?: string | null;
  email?: string | null;
  allowFarmCreation?: boolean | null;
  allowRequisitesWithoutDevice?: boolean | null;
  commission_withdrawal?: number | null;
};

const loadAuthState = (): AuthState | undefined => {
  try {
    const serializedState = localStorage.getItem("auth");
    return serializedState ? JSON.parse(serializedState) : undefined;
  } catch (e) {
    return undefined;
  }
};

const saveAuthState = (state: AuthState) => {
  try {
    localStorage.setItem("auth", JSON.stringify(state));
  } catch (e) {
    console.error("Could not save state", e);
  }
};

export const login = createAsyncThunk(
  "auth/login",
  async (data: AuthForm, { rejectWithValue }) => {
    try {
      const response = await auth(data);
      const decodedToken = jwtDecode<JwtPayload>(response.idToken);
      const roles = decodedToken.roles || ["not_verified"];

      const authData: AuthState = {
        accessToken: response.idToken,
        roles,
        userId: decodedToken.user_id,
        email: decodedToken.email,
        status: decodedToken.status,
        allowFarmCreation: decodedToken.allow_farm_creation,
        allowRequisitesWithoutDevice:
          decodedToken.allow_requisites_without_device,
        commission_withdrawal: decodedToken.commission_withdrawal,
      };

      if ("refreshToken" in response) {
        authData.refreshToken = response.refreshToken;
      } else if (decodedToken.sign_in_second_factor !== "totp") {
        authData.roles = ["need_verify_otp"];
        authData.qrCodeUrl = response.qrCodeUrl;
        authData.sessionInfo = response.sessionInfo;
      }

      return authData;
    } catch (error) {
      if (error instanceof FetchClientError) {
        return rejectWithValue(error.data ?? error.message);
      }
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }

      return rejectWithValue("Произошла неизвестная ошибка");
    }
  }
);

export const enrollmentOtp = createAsyncThunk(
  "auth/enrollmentOtp",
  async (data: EnrollmentOtpForm, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: AuthState };

      if (!auth.accessToken || !auth.sessionInfo) {
        throw new Error("access token not exist");
      }

      const response = await enrollmentOtpApi({
        code: data.code,
        accessToken: auth.accessToken,
        sessionInfo: auth.sessionInfo,
      });

      const decodedToken = jwtDecode<JwtPayload>(response.idToken);
      const roles = decodedToken.roles || ["not_verified"];

      const authData = {
        accessToken: response.idToken,
        refreshToken: response.refreshToken,
        userId: decodedToken.user_id,
        email: decodedToken.email,
        status: decodedToken.status,
        roles,
        qrCodeUrl: null,
        sessionInfo: null,
        allowFarmCreation: decodedToken.allow_farm_creation,
        allowRequisitesWithoutDevice:
          decodedToken.allow_requisites_without_device,
      };

      return authData;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }

      return rejectWithValue("Произошла неизвестная ошибка");
    }
  }
);

export const refreshAccessToken = createAsyncThunk(
  "auth/refresh",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState() as { auth: AuthState };
      if (!auth.refreshToken) throw new Error("No refresh token");

      const response = await refreshToken(auth.refreshToken);

      const decodedToken = jwtDecode<JwtPayload>(response.access_token);
      const roles = decodedToken.roles || ["not_verified"];

      const newAuthData: AuthState = {
        ...auth,
        accessToken: response.access_token,
        roles,
        userId: decodedToken.user_id,
        email: decodedToken.email,
        refreshToken: response.refresh_token,
        status: decodedToken.status,
        allowFarmCreation: decodedToken.allow_farm_creation,
        allowRequisitesWithoutDevice:
          decodedToken.allow_requisites_without_device,
        commission_withdrawal: decodedToken.commission_withdrawal,
      };

      return newAuthData;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }

      return rejectWithValue("Произошла неизвестная ошибка");
    }
  }
);

export const register = createAsyncThunk(
  "auth/register",
  async (data: RegisterForm, { rejectWithValue }) => {
    try {
      const response = await registerApi(data);

      return response;
    } catch (error) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }

      return rejectWithValue("Произошла неизвестная ошибка");
    }
  }
);

const initialState: AuthState = loadAuthState() || {
  roles: null,
  status: null,
  accessToken: null,
  refreshToken: null,
  qrCodeUrl: null,
  sessionInfo: null,
  allowFarmCreation: null,
  allowRequisitesWithoutDevice: null,
  commission_withdrawal: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.roles = null;
      state.status = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.qrCodeUrl = null;
      state.sessionInfo = null;
      state.allowFarmCreation = null;
      state.allowRequisitesWithoutDevice = null;
      state.commission_withdrawal = null;
      state.userId = null;
      state.email = null;
      saveAuthState(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.fulfilled, (state, action: PayloadAction<AuthState>) => {
        Object.assign(state, action.payload);
        saveAuthState(state);
      })
      .addCase(
        enrollmentOtp.fulfilled,
        (state, action: PayloadAction<AuthState>) => {
          Object.assign(state, action.payload);
          saveAuthState(state);
        }
      )
      .addCase(enrollmentOtp.rejected, (state, action) => {
        if (
          action.error.message === "access token not exist" ||
          action.error.message === "INVALID_ID_TOKEN"
        ) {
          state.roles = null;
          state.status = null;
          state.accessToken = null;
          state.refreshToken = null;
          state.qrCodeUrl = null;
          state.sessionInfo = null;
          state.userId = null;
          state.email = null;
          state.allowFarmCreation = null;
          state.allowRequisitesWithoutDevice = null;
          state.commission_withdrawal = null;
          saveAuthState(state);
        }
      })
      .addCase(
        refreshAccessToken.fulfilled,
        (state, action: PayloadAction<AuthState>) => {
          Object.assign(state, action.payload);
          saveAuthState(state);
        }
      )
      .addCase(refreshAccessToken.rejected, (state, action) => {
        if (action.error.message === "refresh token not exist") {
          state.roles = null;
          state.status = null;
          state.accessToken = null;
          state.refreshToken = null;
          state.qrCodeUrl = null;
          state.sessionInfo = null;
          state.userId = null;
          state.email = null;
          state.allowFarmCreation = null;
          state.allowRequisitesWithoutDevice = null;
          state.commission_withdrawal = null;
          saveAuthState(state);
        }
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
